<template>
  <Screen :back="false" title="how to use">
    <!-- Top Heading -->
    <h2 class="fold-heading" aria-label="how to use">
      <span class="fold-heading__shadow">how to use</span>
      <span class="fold-heading__text">how to use</span>
    </h2>
    
    <!-- Steps container -->
    <div class="htu-steps-container">
      
      <!-- Step 1 -->
      <section class="htu-step drafting-border">
        <div class="htu-step__header">
          <h3 class="htu-step__title drafting-text-border">1. Choose a fold</h3>
        </div>
        <div class="htu-step__content">
          <p class="htu-paragraph">
            The first page of the app asks you to choose a fold. Currently there are four popular card folds to choose from. Pick the one that fits your occasion best to get started!
          </p>
        </div>
      </section>

      <!-- Step 2 -->
      <section class="htu-step drafting-border">
        <div class="htu-step__header">
          <h3 class="htu-step__title drafting-text-border">2. Personalize</h3>
        </div>
        <div class="htu-step__content">
          <p class="htu-paragraph">
            This is where the magic happens! Use the action menu to add text boxes or images to your card. You can drag, resize, change fonts, and pick colors to make your masterpiece truly unique.
          </p>
        </div>
      </section>

      <!-- Step 3 -->
      <section class="htu-step drafting-border">
        <div class="htu-step__header">
          <h3 class="htu-step__title drafting-text-border">3. Print</h3>
        </div>
        <div class="htu-step__content">
          <p class="htu-paragraph">
            When your design is perfect, hit the Print button. We'll generate a high-quality PDF specifically scaled for your chosen fold. Send it straight to your home printer on standard paper.
          </p>
          <p class="htu-paragraph" style="margin-top: 16px;">
            <strong>Double-Sided Printing for Bifolds:</strong> If you chose a Bifold Vertical or Bifold Easel, you will get a two-page PDF. Make sure ONLY one piece of paper is in your printer! Print page 1, THEN feed this page back into your printer, printed side up, with the top of the card away from you.
          </p>
        </div>
      </section>

      <!-- Step 4 -->
      <section class="htu-step drafting-border">
        <div class="htu-step__header">
          <h3 class="htu-step__title drafting-text-border">4. Fold</h3>
        </div>
        <div class="htu-step__content">
          <p class="htu-paragraph">
            While it prints, check out the short how-to-fold video that pops up. Grab your freshly printed card, make a few crisp folds along the guides, and you're ready to share your creation!
          </p>
        </div>
      </section>

    </div>

    <AppFooter currentScreen="HowToUseScreen" />

    <!-- Back button in nav -->
    <template v-slot:action>
      <button class="back-btn" @click="$store.commit('setScreen', 'ChooseFoldScreen')">
        <ChevronLeft :size="16" />
        <span>Back to choose folds</span>
      </button>
    </template>
  </Screen>
</template>

<script>
import Screen from './Screen.vue';
import AppFooter from './AppFooter.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronLeft.vue';

export default {
  name: 'HowToUseScreen',
  components: { Screen, ChevronLeft, AppFooter }
}
</script>

<style scoped>
/* ── Hide default nav title — heading is custom ── */
:deep(.title) {
  display: none;
}

/* ── Standalone heading ── */
.fold-heading {
  position: relative;
  font-family: Cabin-Sketch, cursive;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  border: none;
  min-height: 60px;
  width: 90%;
  margin: 0 auto 32px;
  flex-shrink: 0;
  font-size: 80px;
  line-height: 1.1;
  text-align: center;
}

.fold-heading__shadow {
  position: absolute;
  left: 6px;
  top: 6px;
  width: 100%;
  color: var(--surface-darkest);
  -webkit-text-stroke: 0;
}

.fold-heading__text {
  position: relative;
  display: block;
  width: 100%;
  color: var(--green-lightest);
  -webkit-text-stroke: 1px var(--surface-darkest);
  paint-order: stroke fill;
}

/* ── Responsive heading ── */
@media screen and (max-width: 960px) {
  .fold-heading {
    font-size: 64px;
    height: 48px;
    line-height: 48px;
  }
}

@media screen and (max-width: 420px) {
  .fold-heading {
    font-size: 32px;
    height: 24px;
    line-height: 24px;
    margin-bottom: 24px;
  }
}

/* ── Steps Container ── */
.htu-steps-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex: 1;
  padding-bottom: 64px;
}

/* ── Individual Step Row ── */
.htu-step {
  width: 80%;
  max-width: 900px;
  background-color: var(--surface-darker); /* Dark green bands */
  margin: 0 auto 48px;
  padding: 40px 0;
  box-sizing: border-box;
}

/* ── Title (Drafting text outline simulation) ── */
.htu-step__header {
  max-width: 80ch;
  margin: 0 auto 16px;
  padding: 0 48px;
  box-sizing: border-box;
}

.htu-step__title {
  font-family: var(--font-family-balsamiq);
  font-size: 24px;
  line-height: 1.3;
  font-weight: bold;
  color: var(--text-lightest);
  margin: 0;
  /* Simulates black outline on white text like in screenshot */
  text-shadow: 
    -1px -1px 0 var(--surface-darkest),
     1px -1px 0 var(--surface-darkest),
    -1px  1px 0 var(--surface-darkest),
     1px  1px 0 var(--surface-darkest);
}

/* ── Paragraphs (Max 80-char width per line) ── */
.htu-step__content {
  max-width: 80ch;
  margin: 0 auto;
  padding: 0 48px;
  box-sizing: border-box;
}

.htu-paragraph {
  font-family: var(--font-family-balsamiq);
  font-size: 16px;
  line-height: 1.5;
  color: var(--text-bright);
  margin: 0;
}

/* ── Back button ── */
.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-family-balsamiq);
  font-size: 14px;
  line-height: 24px;
  padding: 4px 12px 4px 8px;
  border-radius: 1px;
  border: 2px solid var(--text-bright);
  background-color: var(--surface-darkest);
  color: var(--text-bright);
  filter: url(#button-stroke);
  cursor: pointer;
  white-space: nowrap;
}

.back-btn:hover {
  background-color: var(--surface-dark);
}

/* ── Mobile View ── */
@media screen and (max-width: 560px) {

  /* Padding adjustments for steps */
  .htu-step {
    width: 90%;
    padding: 24px 0;
  }
  .htu-step__header,
  .htu-step__content {
    padding: 0 24px;
  }

}
</style>
